<template>
  <b-container
    fluid
    class="chromatography-graph-print"
  >
    <div class="chromatography-graph-print__title">
      <strong>
        {{ $t('engineering.chromatography.print.title', { screenName }) }}
      </strong>
    </div>
    <s-loader
      v-if="loading"
      size="lg"
    />
    <div v-else>
      <summary-transform-info
        class="my-4"
        :loading.sync="loadingSummary"
      />
      <b-row>
        <b-col
          class="
            mx-3
            mt-3
            chromatography-graph-print__header
          "
          >
          <strong>
            {{ $t('enums.engineering.chromatography.rules.duval') }}
          </strong>
        </b-col>
      </b-row>
      <div class="chromatography-graph-print__graph-row">
        <div
          class="mt-3 chromatography-graph-print__graph-container"
          v-for="(graph, index) in graphs"
          :key="index"
        >
          <polygonal-chart-print
            v-if="graph.coordinates.length > 0"
            :value="graph.coordinates"
            :type="graph.type"
            :title="graph.title"
            :reference-coordinates="getTriangle1Coordinates"
          />
        </div>
      </div>
    </div>
  </b-container>
</template>

<script>
import comparativeAnalysisService from '@/domain/services/installation/engineering/chromatography/comparativeAnalysis/index';
import duvalTriangleCoordinates from '@/domain/services/installation/engineering/chromatography/duvalTriangleCoordinates';
import PolygonalChartPrint from '@/domain/components/installation/engineering/chromatography/polygonalChart/PolygonalChartPrint';
import SummaryTransformInfo from '@/domain/components/installation/supervision/SummaryTransformerInfo';
import LoadingMixin from '@/services/loadingMixin';
import print from '@/services/print';

export default {
  components: {
    SummaryTransformInfo,
    PolygonalChartPrint,
  },
  data: () => ({
    triangles: [],
    graphs: [],
    loadingSummary: false,
  }),
  props: {
    assetModuleId: {
      type: String,
    },
    sampleIds: {
      type: Array,
    },
  },
  mixins: [LoadingMixin],
  computed: {
    screenName() {
      return this.$t('engineering.chromatography.print.graph-screen');
    },
    getTriangle1Coordinates() {
      return this.triangles.triangle1
        && this.triangles.triangle1.map(it => ({
          ...duvalTriangleCoordinates.getTriangle1Coordinates(it),
          name: it.sampleNumber,
        }));
    },
    getTriangle4Coordinates() {
      return this.triangles.triangle4
        && this.triangles.triangle4.map(it => ({
          ...duvalTriangleCoordinates.getTriangle4Coordinates(it),
          name: it.sampleNumber,
        }));
    },
    getTriangle5Coordinates() {
      return this.triangles.triangle5
        && this.triangles.triangle5.map(it => ({
          ...duvalTriangleCoordinates.getTriangle5Coordinates(it),
          name: it.sampleNumber,
        }));
    },
    mountTriangle1Object() {
      const coordinates = this.getTriangle1Coordinates;
      return {
        type: 'duval-triangle-1',
        title: 'enums.engineering.chromatography.methods.duval.triangle-1',
        coordinates,
      };
    },
    mountTriangle4Object() {
      const coordinates = this.getTriangle4Coordinates;
      return {
        type: 'duval-triangle-4',
        title: 'enums.engineering.chromatography.methods.duval.triangle-4',
        coordinates,
      };
    },
    mountTriangle5Object() {
      const coordinates = this.getTriangle5Coordinates;
      return {
        type: 'duval-triangle-5',
        title: 'enums.engineering.chromatography.methods.duval.triangle-5',
        coordinates,
      };
    },
    trianglesObject() {
      return [
        this.mountTriangle1Object,
        this.mountTriangle4Object,
        this.mountTriangle5Object,
      ];
    },
  },
  methods: {
    print,
    callPrint() {
      if (!this.loading && !this.loadingSummary) {
        setTimeout(() => {
          print();
        }, 1000);
      }
    },
    getGraphs() {
      this.startLoading();
      comparativeAnalysisService.getDuvalGraphs(this.assetModuleId, {
        sampleIds: this.sampleIds,
       })
        .then((data) => {
          this.triangles = { ...data };
          this.graphs = this.trianglesObject;
        })
        .finally(() => {
          this.endLoading();
        });
    },
  },
  created() {
    this.getGraphs();
  },
  watch: {
    loadingSummary() {
      this.callPrint();
    },
  },
};
</script>
<style lang="sass" scoped>
  @media print
    @page
      size: A4 !important
  .chromatography-graph-print
    padding: 10px 20px
    &__title
      font-size: 1.2rem
      padding: 10px
      text-align: center
    &__header
      background: var(--color-background-dark)
      padding: 3px 15px
    &__graph-row
      display: table
      text-align: center
    &__graph-container
      display: inline-block
      padding-right: 15px
      padding-left: 15px
</style>
